import { defineComponent, onMounted, ref } from 'vue'
import {
  getProductList,
  getProductDetail,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImage,
  deleteProductImage,
  sortProductImage,
  getUploadTempSignature
} from './api'

const PRODUCT_TYPES = [
  { value: 'normal', label: '普通商品' },
  { value: 'qr_card', label: '二维码卡片' },
  { value: 'tombstone', label: '墓碑定制' },
  { value: 'panorama_vr', label: '全景 VR' }
]

function pickProductId(item) {
  if (!item) return null
  const id = item.id != null ? item.id : item.product_id != null ? item.product_id : item.productId
  if (id == null) return null
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

function pickImageId(item) {
  if (!item) return null
  const id = item.image_id != null ? item.image_id : item.id != null ? item.id : item.imageId
  if (id == null) return null
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

function pickSortOrder(item) {
  if (!item) return 0
  const raw =
    item.sort_order != null
      ? item.sort_order
      : item.sortOrder != null
        ? item.sortOrder
        : item.sort
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

function pickImageUrl(item) {
  if (item == null) return ''
  if (typeof item === 'string') return item.trim()
  if (item.image && typeof item.image === 'object') {
    const nested = pickImageUrl(item.image)
    if (nested) return nested
  }
  const raw =
    item.url != null
      ? item.url
      : item.image_url != null
        ? item.image_url
        : item.imageUrl != null
          ? item.imageUrl
          : item.img_url != null
            ? item.img_url
            : item.imgUrl != null
              ? item.imgUrl
              : item.image != null
                ? item.image
                : item.path != null
                  ? item.path
                  : item.file_url != null
                    ? item.file_url
                    : item.fileUrl != null
                      ? item.fileUrl
                      : item.cos_url != null
                        ? item.cos_url
                        : item.cosUrl != null
                          ? item.cosUrl
                          : item.src != null
                            ? item.src
                            : item.thumbnail != null
                              ? item.thumbnail
                              : item.thumb_url != null
                                ? item.thumb_url
                                : item.link
  const url = String(raw == null ? '' : raw).trim()
  return url
}

function resolveImageUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
    return url
  }
  if (url.startsWith('//')) return `https:${url}`
  const base = (process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
  if (url.startsWith('/') && base) return `${base}${url}`
  return url
}

function extractImagesFromDetail(data) {
  if (!data) return []
  const raw = data.images ?? data.product_images ?? data.image_list ?? data.imageList
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return []
}

function normalizeImageList(list) {
  const rows = (list || []).map((item, index) => {
    if (typeof item === 'string') {
      return {
        image_url: resolveImageUrl(item.trim()),
        sort_order: index
      }
    }
    const imageId = pickImageId(item)
    const imageUrl = resolveImageUrl(pickImageUrl(item))
    return {
      ...item,
      id: imageId != null ? imageId : item.id,
      image_id: imageId,
      url: imageUrl,
      image_url: imageUrl,
      sort_order: pickSortOrder(item)
    }
  })
  rows.sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
    const aid = pickImageId(a) || 0
    const bid = pickImageId(b) || 0
    return aid - bid
  })
  return rows
}

function formatPrice(price) {
  const n = Number(price)
  return Number.isFinite(n) ? n.toFixed(2) : '-'
}

function isValidProductType(type) {
  return PRODUCT_TYPES.some((item) => item.value === type)
}

function formatProductType(type) {
  const matched = PRODUCT_TYPES.find((item) => item.value === type)
  return matched ? matched.label : '普通商品'
}

function isMainImageItem(img, list) {
  if (!img || !list?.length) return false
  let main = list[0]
  for (const item of list) {
    const sort = pickSortOrder(item)
    const mainSort = pickSortOrder(main)
    if (sort < mainSort) {
      main = item
      continue
    }
    if (sort === mainSort && (pickImageId(item) || 0) < (pickImageId(main) || 0)) {
      main = item
    }
  }
  return pickImageId(img) === pickImageId(main)
}

export default defineComponent({
  name: 'ProductsView',
  setup() {
    const productList = ref([])
    const selectedProductType = ref('')
    const loading = ref(false)
    const saving = ref(false)
    const deleting = ref(false)
    const uploading = ref(false)
    const sortSavingId = ref(null)
    const expandedProductId = ref(null)
    const expandedLoading = ref(false)

    const showCreateForm = ref(false)
    const createName = ref('')
    const createDescription = ref('')
    const createPrice = ref('')
    const createProductType = ref('normal')

    const detailVisible = ref(false)
    const detailLoading = ref(false)
    const currentProduct = ref(null)
    const imageList = ref([])

    const editName = ref('')
    const editDescription = ref('')
    const editPrice = ref('')
    const editProductType = ref('normal')

    const fileInputRef = ref(null)
    const uploadTargetProductId = ref(null)
    const previewVisible = ref(false)
    const previewUrl = ref('')

    const fetchProductList = async () => {
      loading.value = true
      try {
        const params = selectedProductType.value
          ? { product_type: selectedProductType.value }
          : {}
        const response = await getProductList(params)
        productList.value = response.data || []
      } catch (error) {
        console.error('获取商品列表失败:', error)
        alert(error.message || '获取商品列表失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const loadExpandedImages = async (productId) => {
      expandedLoading.value = true
      try {
        const response = await getProductDetail(productId)
        const data = response.data || {}
        imageList.value = normalizeImageList(extractImagesFromDetail(data))
      } catch (error) {
        console.error('获取商品图片失败:', error)
        imageList.value = []
        alert(error.message || '获取商品图片失败，请稍后重试')
        throw error
      } finally {
        expandedLoading.value = false
      }
    }

    const isProductExpanded = (item) => {
      const id = pickProductId(item)
      return id != null && expandedProductId.value === id
    }

    const toggleProductExpand = async (item) => {
      const productId = pickProductId(item)
      if (productId == null) return

      if (expandedProductId.value === productId) {
        expandedProductId.value = null
        imageList.value = []
        return
      }

      expandedProductId.value = productId
      if (Array.isArray(item.images)) {
        imageList.value = normalizeImageList(item.images)
        return
      }
      imageList.value = []
      try {
        await loadExpandedImages(productId)
      } catch {
        expandedProductId.value = null
        imageList.value = []
      }
    }

    const syncExpandedImagesFromList = (productId) => {
      if (expandedProductId.value !== productId) return
      const item = productList.value.find((p) => pickProductId(p) === productId)
      if (item && Array.isArray(item.images)) {
        imageList.value = normalizeImageList(item.images)
      }
    }

    const refreshExpandedIfNeeded = async (productId) => {
      if (expandedProductId.value !== productId) return
      syncExpandedImagesFromList(productId)
      const item = productList.value.find((p) => pickProductId(p) === productId)
      if (!item || !Array.isArray(item.images)) {
        await loadExpandedImages(productId)
      }
    }

    const resetCreateForm = () => {
      createName.value = ''
      createDescription.value = ''
      createPrice.value = ''
      createProductType.value = selectedProductType.value || 'normal'
    }

    const openCreateForm = () => {
      resetCreateForm()
      showCreateForm.value = true
    }

    const closeCreateForm = () => {
      showCreateForm.value = false
      resetCreateForm()
    }

    const handleCreate = async () => {
      const name = createName.value.trim()
      const description = createDescription.value.trim()
      const price = Number(createPrice.value)
      const productType = createProductType.value || 'normal'
      if (!name || !description) {
        alert('商品名称和描述不能为空')
        return
      }
      if (!Number.isFinite(price) || price < 0) {
        alert('请输入有效的商品价格')
        return
      }
      if (!isValidProductType(productType)) {
        alert('请选择有效的商品类型')
        return
      }

      saving.value = true
      try {
        const response = await createProduct({
          name,
          description,
          price,
          product_type: productType
        })
        const productId = response.data?.id
        alert(productId ? `商品创建成功，ID：${productId}` : '商品创建成功')
        closeCreateForm()
        await fetchProductList()
        if (productId) {
          const item = productList.value.find((p) => pickProductId(p) === productId)
          if (item) {
            await toggleProductExpand(item)
          }
        }
      } catch (error) {
        console.error('创建商品失败:', error)
        alert(error.message || '创建商品失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const loadProductDetail = async (productId) => {
      detailLoading.value = true
      try {
        const response = await getProductDetail(productId)
        const data = response.data || {}
        currentProduct.value = data
        editName.value = data.name || ''
        editDescription.value = data.description || ''
        editPrice.value = data.price != null ? String(data.price) : ''
        editProductType.value = isValidProductType(data.product_type)
          ? data.product_type
          : 'normal'
      } catch (error) {
        console.error('获取商品详情失败:', error)
        alert(error.message || '获取商品详情失败，请稍后重试')
        throw error
      } finally {
        detailLoading.value = false
      }
    }

    const openDetail = async (productOrId) => {
      const productId =
        typeof productOrId === 'object' ? pickProductId(productOrId) : Number(productOrId)
      if (productId == null) {
        alert('缺少商品 ID')
        return
      }
      detailVisible.value = true
      detailLoading.value = true
      try {
        await loadProductDetail(productId)
      } catch {
        detailVisible.value = false
        currentProduct.value = null
      }
    }

    const closeDetail = () => {
      detailVisible.value = false
      currentProduct.value = null
    }

    const handleProductDelete = async (item) => {
      const productId = pickProductId(item)
      if (productId == null) {
        alert('缺少商品 ID')
        return
      }
      const name = item.name || `ID ${productId}`
      if (!confirm(`确定删除商品「${name}」吗？`)) return

      deleting.value = true
      try {
        await deleteProduct(productId)
        alert('商品已删除')
        if (expandedProductId.value === productId) {
          expandedProductId.value = null
          imageList.value = []
        }
        if (pickProductId(currentProduct.value) === productId) {
          closeDetail()
        }
        await fetchProductList()
      } catch (error) {
        console.error('删除商品失败:', error)
        alert(error.message || '删除商品失败，请稍后重试')
      } finally {
        deleting.value = false
      }
    }

    const handleUpdate = async () => {
      const productId = pickProductId(currentProduct.value)
      if (productId == null) {
        alert('缺少商品 ID')
        return
      }
      const name = editName.value.trim()
      const description = editDescription.value.trim()
      const price = Number(editPrice.value)
      const productType = editProductType.value || 'normal'
      if (!name || !description) {
        alert('商品名称和描述不能为空')
        return
      }
      if (!Number.isFinite(price) || price < 0) {
        alert('请输入有效的商品价格')
        return
      }
      if (!isValidProductType(productType)) {
        alert('请选择有效的商品类型')
        return
      }

      saving.value = true
      try {
        await updateProduct({
          product_id: productId,
          name,
          description,
          price,
          product_type: productType
        })
        alert('商品信息已更新')
        await loadProductDetail(productId)
        await fetchProductList()
      } catch (error) {
        console.error('更新商品失败:', error)
        alert(error.message || '更新商品失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const extractPresignedUrl = (signatureResponse) => {
      const source = signatureResponse?.data || signatureResponse || {}
      const presignedUrl = source.url
      if (!presignedUrl) {
        throw new Error('上传凭证缺少 url 字段，请检查接口返回')
      }
      return presignedUrl
    }

    const uploadToCos = async (file) => {
      const signatureResponse = await getUploadTempSignature(file.name)
      const presignedUrl = extractPresignedUrl(signatureResponse)
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      })
      if (!uploadResponse.ok) {
        throw new Error(`上传 COS 失败: ${uploadResponse.status}`)
      }
      return presignedUrl.split('?')[0]
    }

    const triggerImageUpload = (item) => {
      const productId = pickProductId(item)
      if (productId == null) {
        alert('缺少商品 ID')
        return
      }
      uploadTargetProductId.value = productId
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
        fileInputRef.value.click()
      }
    }

    const handleSelectImage = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const productId = uploadTargetProductId.value
      if (productId == null) {
        alert('缺少商品 ID')
        return
      }

      uploading.value = true
      try {
        const imageUrl = await uploadToCos(file)
        const maxSort = imageList.value.reduce(
          (max, img) => Math.max(max, pickSortOrder(img)),
          -1
        )
        await uploadProductImage({
          product_id: productId,
          image_url: imageUrl,
          sort_order: maxSort + 1
        })
        alert('图片上传成功')
        await fetchProductList()
        await refreshExpandedIfNeeded(productId)
      } catch (error) {
        console.error('上传商品图片失败:', error)
        alert(error.message || '上传图片失败，请稍后重试')
      } finally {
        uploading.value = false
        uploadTargetProductId.value = null
      }
    }

    const handleImageSortSave = async (image) => {
      const imageId = pickImageId(image)
      if (imageId == null) {
        alert('缺少图片 ID')
        return
      }
      const sortOrder = pickSortOrder(image)
      if (!Number.isFinite(sortOrder) || sortOrder < 0) {
        alert('请输入有效的顺序 ID（≥0）')
        return
      }

      const productId = expandedProductId.value
      sortSavingId.value = imageId
      try {
        const response = await sortProductImage(imageId, sortOrder)
        const data = response.data || {}
        image.sort_order =
          data.sort_order != null ? Number(data.sort_order) : sortOrder
        if (productId != null) {
          await fetchProductList()
          await refreshExpandedIfNeeded(productId)
        }
      } catch (error) {
        console.error('更新图片排序失败:', error)
        if (productId != null) {
          await fetchProductList()
          await refreshExpandedIfNeeded(productId)
        }
        alert(error.message || '更新排序失败，请稍后重试')
      } finally {
        sortSavingId.value = null
      }
    }

    const isMainImage = (img) => isMainImageItem(img, imageList.value)

    const handleImageDelete = async (image) => {
      const imageId = pickImageId(image)
      if (imageId == null) {
        alert('缺少图片 ID')
        return
      }
      if (!confirm('确定删除这张商品图片吗？')) return

      const productId = expandedProductId.value
      try {
        await deleteProductImage(imageId)
        alert('图片已删除')
        await fetchProductList()
        if (productId != null) {
          await refreshExpandedIfNeeded(productId)
        }
      } catch (error) {
        console.error('删除商品图片失败:', error)
        alert(error.message || '删除图片失败，请稍后重试')
      }
    }

    const openPreview = (imageUrl) => {
      previewUrl.value = imageUrl
      previewVisible.value = true
    }

    const closePreview = () => {
      previewVisible.value = false
      previewUrl.value = ''
    }

    onMounted(() => {
      fetchProductList()
    })

    return {
      PRODUCT_TYPES,
      productList,
      selectedProductType,
      loading,
      saving,
      deleting,
      uploading,
      sortSavingId,
      expandedLoading,
      showCreateForm,
      createName,
      createDescription,
      createPrice,
      createProductType,
      detailVisible,
      detailLoading,
      currentProduct,
      imageList,
      editName,
      editDescription,
      editPrice,
      editProductType,
      fileInputRef,
      previewVisible,
      previewUrl,
      pickProductId,
      pickImageId,
      pickImageUrl,
      formatPrice,
      formatProductType,
      fetchProductList,
      isProductExpanded,
      toggleProductExpand,
      openCreateForm,
      closeCreateForm,
      handleCreate,
      openDetail,
      closeDetail,
      handleProductDelete,
      handleUpdate,
      triggerImageUpload,
      handleSelectImage,
      handleImageSortSave,
      isMainImage,
      handleImageDelete,
      openPreview,
      closePreview
    }
  }
})
